import React, { useEffect, useRef, useCallback } from "react";
import axios from "axios";
import TABLE_DATA from "./tableDataSet";
import { DiffFunction, DiffFunctionDeleteRow, DiffFunctionDeleteSO } from "./diff";
import update from "immutability-helper";
import { ClearableInput } from "./component/Input";
import { table_render } from './pdfGen.js';

const useDidMountEffect = (func, deps) => {
    const didMount = useRef(false);

    useEffect(() => {
        if (didMount.current) func();
        else didMount.current = true;
    }, deps);
};

const post = async (url, data) => {
    try {
        const response = await axios.post(process.env.REACT_APP_API + url, data);
        return response;
    } catch (e) {
        throw new Error(e);
    }
};

const getURLParam = () => {
    let s = window.location.search;
    s = s.replace(/^\?/, "");
    let ss = s.split("&");
    let obj = {};
    for (let i of ss) {
        if (!!i) {
            let name = i.split("=")[0];
            let value = i.split("=")[1];
            obj[name] = decodeURI(value);
        }
    }
    return obj;
};

/**
 *
 * @param {string} api api 주소 process.env.REACT_APP_API 제외 [string]
 * @param {object} body api body {}
 * @param {string} name 다운로드 파일 이름
 * @returns
 */
async function DownloadFile({ api = "", body = {}, name = null }) {
    const fileName = name ?? body?.name ?? null;
    if (!!!api) {
        return;
    }
    const response = await axios({
        url: process.env.REACT_APP_API + api,
        data: body,
        method: "POST",
        responseType: "blob",
    });
    const blob = new Blob([response.data]);

    const fileObjectUrl = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = fileObjectUrl;
    link.style.display = "none";

    link.download = fileName;

    document.body.appendChild(link);
    link.click();
    link.remove();

    window.URL.revokeObjectURL(fileObjectUrl);
    return true;
}

const RPAD = (str, padLen, padStr = "_") => {
    if (padStr.length > padLen) {
        return str;
    }
    str += ""; // 문자로
    padStr += ""; // 문자로
    while (str.length < padLen) str = str + padStr;
    str = str.length >= padLen ? str.substring(0, padLen) : str;
    return str;
};

/**
 * 
 * @param {string} str 
 * @param {number} padLen 
 * @param {*} padStr 
 * @returns {string}
 */
const LPAD = (str, padLen, padStr) => {
    if (padStr.length > padLen) {
        return str;
    }
    str += ""; // 문자로
    padStr += ""; // 문자로
    while (str.length < padLen) str = padStr + str;
    str = str.length >= padLen ? str.substring(0, padLen) : str;
    return str;
};

const CKEditorOptions = {
    toolbar: [
        'undo',
        'redo',
        '|',
        'heading',
        '|',
        'alignment',
        {
            label: "text",
            icon: "text",
            items: [
                'fontSize',
                'fontColor',
                'fontFamily',
                'fontBackgroundColor',
                'highlight',
                'bold',
                'italic',
                'underline',
                'strikethrough',
            ]
        },
        {
            label: "align",
            icon: 'alignLeft',
            items: [
                'numberedList',
                'bulletedList',
                'indent',
                'outdent'
            ]
        },
        '|',
        'insertTable',
        'imageInsert',
        {
            label: "plus",
            icon: "plus",
            items: [
                'subscript',
                'superscript',
                'specialCharacters',
                'link',
                'blockQuote',
                'horizontalLine',
            ]
        },
        '|',
        'pageBreak',


    ],
    fontSize: {
        options: [8, 9, 10, 11, 12, 14, 16, 18, 20, 22, 26, 28, 36, 48],
    },
    table: {
        contentToolbar: [
            "toggleTableCaption",
            "tableColumn",
            "tableRow",
            "mergeTableCells",
            "tableCellProperties",
            "tableProperties",
        ],
    },
    heading: {
        options: [
            { model: "paragraph", title: "Paragraph", class: "ck-heading_paragraph" },
            { model: "heading1", view: "h1", title: "Heading 1", class: "ck-heading_heading1" },
            { model: "heading2", view: "h2", title: "Heading 2", class: "ck-heading_heading2" },
            { model: "heading3", view: "h3", title: "Heading 3", class: "ck-heading_heading3" },
            { model: "heading4", view: "h4", title: "Heading 4", class: "ck-heading_heading4" },
        ],
    },
    htmlSupport: {
        allow: [
            {
                name: /.*/,
                attributes: {
                    "data-uniquecode": true,
                    "data-reviewdata": true,
                    "data-reviewcomplete": true,
                },
            },
        ],
    },
    removePlugins: ["MediaEmbedToolbar"],
};

const focusContentEditableTextToEnd = (element) => {
    if (element.innerText.length === 0) {
        element.focus();

        return;
    }

    const selection = window.getSelection();
    const newRange = document.createRange();
    newRange.selectNodeContents(element);
    newRange.collapse(false);
    selection?.removeAllRanges();
    selection?.addRange(newRange);
};

/**
 * 
 * @param {Date} inDate 
 * @param {boolean} onlyDate 
 * @returns 
 */
const dateToDateFormat = (inDate = new Date(), onlyDate = false) => {
    const date = new Date(inDate);
    let month = date.getMonth() + 1;
    let day = date.getDate();
    let hour = date.getHours();
    let minute = date.getMinutes();
    let second = date.getSeconds();
    let milsecond = date.getMilliseconds();

    month = month >= 10 ? month : "0" + month;
    day = day >= 10 ? day : "0" + day;
    hour = hour >= 10 ? hour : "0" + hour;
    minute = minute >= 10 ? minute : "0" + minute;
    second = second >= 10 ? second : "0" + second;

    if (onlyDate) {
        return date.getFullYear() + "." + month + "." + day;
    } else {
        return (
            date.getFullYear() +
            "-" +
            month +
            "-" +
            day +
            " " +
            hour +
            ":" +
            minute +
            ":" +
            second +
            "." +
            LPAD(milsecond, 3, "0")
        );
    }
};

const dateToDateFormat2 = (inDate = new Date(), onlyDate = false) => {
    const date = new Date(inDate);
    let month = date.getMonth() + 1;
    let day = date.getDate();
    let hour = date.getHours();
    let minute = date.getMinutes();
    let second = date.getSeconds();
    let milsecond = date.getMilliseconds();

    month = month >= 10 ? month : "0" + month;
    day = day >= 10 ? day : "0" + day;


    if (onlyDate) {
        return date.getFullYear() + "." + month + "." + day;
    } else {
        return (
            date.getFullYear() +
            "-" +
            month +
            "-" +
            day +
            " " +
            23 +
            ":" +
            59 +
            ":" +
            59 +
            "." +
            LPAD(milsecond, 3, "0")
        );
    }
};

const modifyTableData = (type, originData) => {
    let targetForm = [...TABLE_DATA?.[type]];
    let fidx = -1;
    Object.keys(originData).forEach((k) => {
        fidx = targetForm.findIndex((i) => i.name === k);
        if (fidx > -1) {
            targetForm = update(targetForm, {
                [fidx]: { $merge: { value: originData[k] } },
            });

            // if (k === "unit") {
            //     if (k?.unitType?.toString() === "1") {
            //         targetForm = update(targetForm, {
            //             [fidx]: { $merge: { type: "text", value: "" } },
            //         });
            //     }
            // }
        }
    });

    return targetForm;
};

const userAuthCF = (auth) => {
    const isAdmin = auth?.filter((e) => e.code.toString() === "99" || e.code.toString() === "0").length > 0 || false;
    const isReader = auth?.filter((e) => e.code.toString() === "1" || e.code.toString() === "0").length > 0 || false;
    const isWriter = auth?.filter((e) => e.code.toString() === "2" || e.code.toString() === "0").length > 0 || false;
    const isReviewer = auth?.filter((e) => e.code.toString() === "3" || e.code.toString() === "0").length > 0 || false;
    const isSuperVisor = auth?.filter((e) => e.code.toString() === "4" || e.code.toString() === "0").length > 0 || false;
    const isExternal = auth?.filter((e) => e.code.toString() === "20").length > 0 || false;
    const isReady = auth?.filter((e) => e.code.toString() === "99" || e.code.toString() === "4" || e.code.toString() === "0").length > 0 || false;

    return { isAdmin, isWriter, isReader, isReviewer, isExternal, isSuperVisor,isReady };
};

const generateRandomString = (length = 4) => {
    let result = "";
    const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    const charactersLength = characters.length;
    let counter = 0;
    while (counter < length) {
        result += characters.charAt(Math.floor(Math.random() * charactersLength));
        counter += 1;
    }
    return result;
};


const useDebounce = () => {
    //timeout을 clear 해주기 위해 ref에 Timer를 저장
    const schedule = useRef();
    
    //함수를 받아 새로운 함수를 반환해주는 currying 구조의 함수를 반환하고 있다.
    return useCallback(
      (callback = () => {}, delay = 500 /*ms*/) =>
        (...arg) => {
          //함수가 호출되면 기존 timeout을 초기화하고 새로운 timeout을 생성한다.
          clearTimeout(schedule.current);
          schedule.current = setTimeout(() => callback(...arg), delay);
        },
      [],
    );
  };

  const useThrottle = () => {
    //현재 함수 실행 후 timeout을 기다리는 상태인지 나타내는 boolean
    const isWaiting = useRef(false);
  
    return useCallback(
      (callback = (...arg) => {}, delay = 500) =>
        (...arg) => {
          //대기 상태라면 아무것도 하지 않고, 대기상태가 아니라면 아래 코드 실행
          if (!isWaiting.current) {
            //callback 함수 실행
            callback(...arg);
            //대기상태로 변경
            isWaiting.current = true;
            //delay 이후 대기상태 해제
            setTimeout(() => {
              isWaiting.current = false;
            }, delay);
          }
        },
      [],
    );
  };

export {
    useDidMountEffect,
    post,
    getURLParam,
    DownloadFile,
    CKEditorOptions,
    TABLE_DATA,
    DiffFunction,
    DiffFunctionDeleteRow,
    DiffFunctionDeleteSO,
    RPAD,
    LPAD,
    focusContentEditableTextToEnd,
    dateToDateFormat,
    dateToDateFormat2,
    modifyTableData,
    ClearableInput,
    userAuthCF,
    generateRandomString,
    table_render,
    useDebounce,
    useThrottle
};
